import React, { DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from '@producer/ui-kit';

import { formatPicture } from '../../../core/services/image.service';
import { PhotoUploader } from '../../../components/photo-uploader/photo-uploader';
import { Storyboard } from '../../../app/entities/storyboard';
import { Shot } from '../../../app/entities/shot';
import { LimitReachedModal } from '../../../components/dialogs/limit-reached.modal';
import { openModal } from '../../../core/modal/open-modal';
import { selectFiles } from '../../../core/services/file.service';

import styles from './create-shot-card.module.scss';
type CreateShotCardProps = {
  storyboard: Storyboard;
  zoom: number;
};

export const CreateShotCard: React.FC<CreateShotCardProps> = ({ storyboard, zoom }) => {
  const { t } = useTranslation('project');

  const handleCreate = async (file?: File) => {
    if (!storyboard.project?.canAddShot()) {
      return openModal(LimitReachedModal, {
        type: 'shots',
        eventName: 'Shots exceed 10',
      });
    }

    let frame;

    if (file) {
      if (file.type === 'image/gif') {
        frame = {
          data: file,
          size: file.size,
          type: file.type,
        };
      } else {
        const options = { width: 640, height: 380 };
        frame = await formatPicture(file, options);
      }
    }

    const shot = await Shot.createShot({ projectId: storyboard.projectId, file: frame });

    // `Shot.createShot` swallows API failures: it toasts and returns undefined. Without this
    // guard, reading `shot._id` threw an uncaught TypeError on top of that toast (PRO-1075).
    if (!shot) return;

    await storyboard.addShot(shot._id);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.persist();

    const files = Array.from(e.dataTransfer.files);

    for (const file of files) {
      await handleCreate(file);
    }
  };

  const handleFilesChange = async (files: File[]) => {
    for (const file of files) {
      await handleCreate(file);
    }
  };

  const openFileNavigator = async () => {
    const files = await selectFiles({
      multiple: true,
      accept: 'image/*',
    });

    if (!files || files.length === 0) {
      return;
    }

    handleFilesChange(files);
  };

  return (
    <div
      className={styles['container']}
      data-zoom={zoom}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => zoom > 2 && openFileNavigator()}
    >
      {zoom <= 2 && (
        <PhotoUploader
          onMultipleChange={handleFilesChange}
          buttonName="New shot"
          placeholderTitle={`${t('drag')} ${t('or')} ${t('browse_files')}`}
          placeholderDescription="JPG, PNG"
          multiple
        />
      )}

      {zoom > 2 && (
        <React.Fragment key="create-shot-card">
          <div className={styles['title']}>New shot</div>

          <div className={styles['folder-container-accepted-files-container']}>
            <div className={styles['folder-container-accepted-files']}>
              {t('accepted')}: .jpg, .png
            </div>
          </div>

          <div className={styles['folder-container-upload']}>
            <Upload />

            <p className={styles['folder-container-upload-drag-and-drop']}>{t('drag')}</p>

            <p className={styles['folder-container-upload-drag-and-drop']}>{t('or')}</p>

            <div className={styles['folder-container-upload-browse']}>{t('browse_files')}</div>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};
